import {
    dehydrate,
    DehydratedState,
    QueryClient,
    useQuery,
} from "@tanstack/react-query";
import type { GetServerSideProps, NextPage } from "next";
import { ESeoPage } from "@/utils/mockData/metaMocks";
import Seo from "@/components/common/Seo";
import { getOpenGraphDataTourDestinations } from "@/utils/seo";
import { withRedirect } from "@/utils/redirects";
import ToursDestinationsBody from "../components/search/ToursDestinationsBody";
import {
    getBrochureData,
    getFiltersData,
    getInitialPackageData,
    getNavBarData,
    getSeoInfo,
    getToursShopPage,
} from "../utils/queries";

// Helper per pulire i valori dei parametri query rimuovendo caratteri non validi e parametri UTM
const cleanQueryParam = (
    value: string | string[] | undefined
): string | undefined => {
    if (!value) return undefined;
    const param = typeof value === "string" ? value : value[0];
    if (!param) return undefined;

    // Rimuovi caratteri non validi all'inizio (? o &)
    let cleaned = param.trim();
    if (cleaned.startsWith("?") || cleaned.startsWith("&")) {
        cleaned = cleaned.substring(1);
    }

    // Se il valore contiene parametri UTM (inizia con utm_), consideralo vuoto
    if (cleaned.startsWith("utm_")) {
        return undefined;
    }

    // Se il valore contiene "?" (probabilmente parte di un URL malformato), prendi solo la parte prima del "?"
    const questionMarkIndex = cleaned.indexOf("?");
    if (questionMarkIndex !== -1) {
        cleaned = cleaned.substring(0, questionMarkIndex);
    }

    // Se dopo la pulizia è vuoto, ritorna undefined
    return cleaned.length > 0 ? cleaned : undefined;
};

const decodeTagDestination = (
    tagDestinationRaw?: string
): string | undefined => {
    if (!tagDestinationRaw) return undefined;
    const decoded = decodeURIComponent(tagDestinationRaw);
    return decoded.replace(/-and-/g, "--");
};

const ToursDestinations: NextPage<{
    destination: string | undefined | null;
    tourType: string | undefined | null;
    tagDestination: string | undefined | null;
}> = ({ destination, tourType, tagDestination }) => {
    // const { data: metaTags } = useQuery({
    //     queryKey: ["metaTags", ESeoPage.TOURSDESTINATION],
    //     queryFn: () => getSeoInfo(ESeoPage.TOURSDESTINATION),
    // });
    const { data: filtersData } = useQuery({
        queryKey: ["filters-data"],
        queryFn: getFiltersData,
        refetchOnWindowFocus: false,
    });
    const { data: packageData } = useQuery({
        queryKey: ["package-data"],
        queryFn: () =>
            getInitialPackageData(destination, tourType, tagDestination, "en"),
        refetchOnWindowFocus: false,
    });
    const { data: toursShopPageData } = useQuery({
        queryKey: ["tours-shop-page-data"],
        queryFn: getToursShopPage,
        refetchOnWindowFocus: false,
    });

    const initialSelectedDestination = destination ? [destination] : [];
    const initialSelectedTagDestination = tagDestination || null;
    const initialSelectedTourType = tourType ? [tourType] : [];

    const metadata = getOpenGraphDataTourDestinations(
        toursShopPageData,
        packageData
    );

    return (
        <>
            <Seo
                seoInfo={metadata}
                noindex={!toursShopPageData?.toursShopPage?.metadata?.index}
            />
            {filtersData && (
                <ToursDestinationsBody
                    filtersData={filtersData}
                    packageData={packageData || []}
                    initialSelectedDestination={initialSelectedDestination}
                    initialSelectedTourType={initialSelectedTourType}
                    initialSelectedTagDestination={
                        initialSelectedTagDestination
                    }
                />
            )}
        </>
    );
};

export const getServerSideProps: GetServerSideProps = withRedirect(
    async (
        ctx
    ): Promise<{
        props: {
            dehydratedState: DehydratedState;
            destination: string | undefined | null;
            tourType: string | undefined | null;
            tagDestination: string | undefined | null;
        };
    }> => {
        const queryClient = new QueryClient();

        const destination = cleanQueryParam(ctx.query.destination);
        const tourType = cleanQueryParam(ctx.query.tourType);
        const tagDestinationRaw = cleanQueryParam(ctx.query.tagDestination);
        const tagDestination = decodeTagDestination(tagDestinationRaw);

        await Promise.all([
            await queryClient.prefetchQuery(["navBar"], getNavBarData),
            await queryClient.prefetchQuery(["brochures"], getBrochureData),

            await queryClient.prefetchQuery(
                ["metaTags", ESeoPage.TOURSDESTINATION],
                () => getSeoInfo(ESeoPage.TOURSDESTINATION)
            ),
            await queryClient.prefetchQuery(["filters-data"], getFiltersData),
            await queryClient.prefetchQuery(["package-data"], () =>
                getInitialPackageData(
                    destination,
                    tourType,
                    tagDestination,
                    "en"
                )
            ),
        ]);

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                destination: destination || null,
                tourType: tourType || null,
                tagDestination: tagDestination || null,
            },
        };
    }
);

export default ToursDestinations;
